import type { MouseEvent, ReactNode, RefObject, FC, Ref } from "react";
import { FinancingCapacity } from "../FinancingCapacity";
import { Section } from "../Section";
import {
  Button,
  Card,
  Container,
  type ContainerProps,
  Flex,
  Heading,
  ProgressBar,
  Spacing,
  Text,
  ValueParser,
} from "@huub-web/ui";
import { useThemeSlot } from "@huub-web/ui/hooks/useThemeSlot";
import { useTranslation } from "react-i18next";
import { isNumber, mergeClasses } from "@huub-web/utils";

import { CostCalculation } from "./components/CostCalculation";
import { DefaultMaximumMortgageResultBanner } from "./components/DefaultMaximumMortgageResultBanner";
import { DefaultMaximumMortgageResultComparison } from "./components/DefaultMaximumMortgageResultComparison";
import type { MaximumMortgageCalculationResult } from "../../types/MaximumMortgageWizardResult";
import {
  InterestRatesSimple,
  type InterestRatesSimpleHandle,
  type InterestRatesSimpleProps,
} from "../InterestRatesSimple";
import { ValuesListing } from "./components/ValuesListing";
import { HouseHeader } from "../HouseHeader";

import {
  progressBarStyle,
  containerStyle,
  columnsCardContentStyle,
} from "./MaximumMortgageWizardResult.css";

type MaximumMortgageWizardResultCTA = {
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  tracking?: string;
  href?: string;
  value?: string;
};

export type MaximumMortgageWizardResultCTAKey =
  | "chanceCard"
  | "advisor"
  | "continue"
  | "costCalculation"
  | "yourSituation"
  | "neededSituation"
  | "interestRate"
  | "analysis";

export type MaximumMortgageWizardResultProps = {
  result: MaximumMortgageCalculationResult;
  getInterestRates?: InterestRatesSimpleProps["getInterestRates"];
  getBankImage?: InterestRatesSimpleProps["getBankImage"];
  /**
   * Callback fired when interest rate filter values change
   */
  onInterestRateFiltersChange?: InterestRatesSimpleProps["onFiltersChange"];
  /**
   * Ref to imperatively update interest rate filter values from a parent template.
   */
  interestRatesFilterRef?: RefObject<InterestRatesSimpleHandle | null>;
  /**
   * Initial fixed-rate period shown in the interest rates card.
   * Defaults to "10" to match the rate used in the cost calculation description.
   */
  defaultInterestRatePeriod?: InterestRatesSimpleProps["defaultPeriod"];
  /**
   * Initial NHG toggle state shown in the interest rates card.
   * Defaults to true ("Met") when not provided.
   */
  defaultInterestRateNhg?: InterestRatesSimpleProps["defaultNhg"];
  CTAs?: Partial<
    Record<MaximumMortgageWizardResultCTAKey, MaximumMortgageWizardResultCTA>
  >;
  container?: ContainerProps["size"];
  trailing?: ReactNode;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

export const MaximumMortgageWizardResult: FC<
  MaximumMortgageWizardResultProps
> = ({
  result,
  getInterestRates,
  getBankImage,
  onInterestRateFiltersChange,
  interestRatesFilterRef,
  defaultInterestRatePeriod = "10",
  defaultInterestRateNhg = true,
  CTAs,
  container = "marketing",
  trailing,
  ref,
}) => {
  const { t } = useTranslation();

  const BannerComponent = useThemeSlot({
    key: "maximumMortgageResultBanner",
    defaultComponent: DefaultMaximumMortgageResultBanner,
  });

  const ComparisonComponent = useThemeSlot({
    key: "maximumMortgageResultComparison",
    defaultComponent: DefaultMaximumMortgageResultComparison,
  });

  const resultType = result.resultType;
  const hasPartner = result.values?.hasPartner;
  const isRefinance = result.values?.purpose === "refinance";
  const isRenovation = result.values?.purpose === "renovate";

  const subject = isRefinance
    ? "Refinance"
    : isRenovation
      ? "Renovation"
      : "House";
  const thisSubjectKey = `maxMortgageWizard.result.subject.this${subject}`;
  const theSubjectKey = `maxMortgageWizard.result.subject.the${subject}`;

  return (
    <div ref={ref} className={mergeClasses(containerStyle)}>
      <Section variant="tint" edge="background">
        <Container size={container}>
          {!isRenovation && (
            <HouseHeader
              record={result.house?.record}
              card={
                <>
                  <FinancingCapacity
                    requiredAmountLabel={t(
                      "maxMortgageWizard.result.financing.maxMortgageLabel",
                    )}
                    requiredAmount={result?.maximumLoan ?? 0}
                    maximumCapacityLabel={t(
                      isRefinance
                        ? "maxMortgageWizard.result.financing.neededFundsLabel"
                        : result.values?.houseInMind === "no"
                          ? "maxMortgageWizard.result.financing.desiredLoanLabel"
                          : result.values?.houseInMind === "newly-build"
                            ? "maxMortgageWizard.result.financing.neededMortgageLabel"
                            : "maxMortgageWizard.result.financing.askingPriceLabel",
                    )}
                    maximumCapacity={
                      isRefinance ||
                      result.values?.houseInMind === "newly-build" ||
                      result.values?.houseInMind === "no"
                        ? (result?.totalFinancingNeed ??
                          result?.askingPrice ??
                          0)
                        : (result?.askingPrice ?? 0)
                    }
                  />
                  {isNumber(result.fundablePercentage) &&
                    result.fundablePercentage > 0 && (
                      <ProgressBar
                        className={progressBarStyle}
                        color="gradient"
                        progress={result.fundablePercentage}
                      />
                    )}
                </>
              }
              hasPartner={hasPartner}
              chanceCardCTA={
                CTAs?.chanceCard &&
                result.values?.purpose !== "refinance" &&
                result.values?.houseInMind !== "newly-build" && (
                  <Button
                    variant="background"
                    as={CTAs.chanceCard.href ? "a" : "button"}
                    href={CTAs.chanceCard.href}
                    tracking={CTAs.chanceCard.tracking}
                    onClick={CTAs.chanceCard.onClick}
                  >
                    {CTAs.chanceCard.value ??
                      t(
                        `maxMortgageWizard.result.${resultType}.cta.chanceCard`,
                      )}
                  </Button>
                )
              }
            />
          )}

          <Heading
            variant="main"
            title={t(
              `maxMortgageWizard.result.${resultType}.heading.title.${hasPartner ? "plural" : "singular"}`,
            )}
            description={t(
              `maxMortgageWizard.result.${resultType}.heading.description.${isRefinance ? "refinance." : isRenovation ? "renovation." : ""}${hasPartner ? "plural" : "singular"}`,
              {
                thisSubject: t(thisSubjectKey),
                theSubject: t(theSubjectKey),
                platform: t("platform", { defaultValue: "Huub" }),
              },
            )}
            childrenAfter={
              (result.values?.houseInMind || isRenovation || isRefinance) && (
                <Text as="h2" variant="surfaceSubtitle" color="currentColor75">
                  {t(
                    isRefinance
                      ? "maxMortgageWizard.result.refinance.title"
                      : isRenovation
                        ? "maxMortgageWizard.result.renovation.title"
                        : `maxMortgageWizard.result.houseInMind.${result.values?.houseInMind}`,
                  )}
                </Text>
              )
            }
          />

          {!(
            isRenovation &&
            (result.totalFinancingNeed ?? result.neededLoan) === 0
          ) && (
            <ComparisonComponent
              yourSituationCTA={
                CTAs?.yourSituation && (
                  <Button
                    size="s"
                    alignSelf="flex-start"
                    as={CTAs.yourSituation.href ? "a" : "button"}
                    href={CTAs.yourSituation.href}
                    tracking={CTAs.yourSituation.tracking}
                    onClick={CTAs.yourSituation.onClick}
                  >
                    {CTAs.yourSituation.value ??
                      t(
                        `maxMortgageWizard.result.${resultType}.cta.yourSituation`,
                      )}
                  </Button>
                )
              }
              neededSituationCTA={
                CTAs?.neededSituation && (
                  <Button
                    size="s"
                    alignSelf="flex-start"
                    as={CTAs.neededSituation.href ? "a" : "button"}
                    href={CTAs.neededSituation.href}
                    tracking={CTAs.neededSituation.tracking}
                    onClick={CTAs.neededSituation.onClick}
                  >
                    {CTAs.neededSituation.value ??
                      t(
                        `maxMortgageWizard.result.${resultType}.cta.neededSituation`,
                      )}
                  </Button>
                )
              }
              yourSituationValue={result.maximumLoan}
              yourSituationMonthlyValue={result.monthlyCosts}
              neededSituationValue={
                result.totalFinancingNeed ?? result.neededLoan
              }
              neededSituationMonthlyValue={result.neededMonthlyCosts}
              yourSituationDescription={t(
                `maxMortgageWizard.result.${resultType}.situationComparison.yourSituation.${isRefinance ? "refinance." : ""}${hasPartner ? "plural" : "singular"}`,
                {
                  thisSubject: t(thisSubjectKey),
                  theSubject: t(theSubjectKey),
                },
              )}
              neededSituationTitle={
                isRefinance
                  ? t(
                      "maxMortgageWizard.result.situationComparison.neededRefinanceMortgage",
                    )
                  : isRenovation
                    ? t(
                        "maxMortgageWizard.result.situationComparison.neededRenovationMortgage",
                      )
                    : undefined
              }
              neededSituationDescription={t(
                `maxMortgageWizard.result.${resultType}.situationComparison.neededSituation.${isRefinance ? "refinance" : isRenovation ? "renovation" : result.values?.houseInMind === "newly-build" ? "newHouse" : "existingHouse"}`,
                {
                  thisSubject: t(thisSubjectKey),
                  theSubject: t(theSubjectKey),
                },
              )}
              hasPartner={hasPartner}
              hideNeededSituation={isRenovation}
            />
          )}

          {isNumber(result.ownFundsNeeded) && result.ownFundsNeeded > 0 && (
            <Card
              elevated
              edgeDirection="horizontal"
              contentClassName={columnsCardContentStyle}
            >
              <Flex direction="column" gap="xs">
                <Text ellipsis variant="bodyS">
                  {t(
                    `maxMortgageWizard.result.ownFundsNeeded.${isRefinance ? "refinance" : isRenovation ? "renovation" : "house"}.label`,
                  )}
                </Text>
                <Text variant="displayL" color={"primary"} bold>
                  <ValueParser
                    value={{ value: result.ownFundsNeeded, type: "money" }}
                  />
                </Text>
              </Flex>
              <Text color="currentColor50" markdown grow>
                {t(
                  `maxMortgageWizard.result.ownFundsNeeded.${isRefinance ? "refinance" : isRenovation ? "renovation" : "house"}.description`,
                )}
              </Text>
            </Card>
          )}

          {!!result.house?.record.id && CTAs?.analysis && (
            <Card
              border="primary"
              edgeDirection="horizontal"
              contentClassName={columnsCardContentStyle}
            >
              <Text color="currentColor50" markdown grow>
                {t("maxMortgageWizard.result.analysis.description")}
              </Text>
              <Button
                as={CTAs.analysis.href ? "a" : "button"}
                href={CTAs.analysis.href}
                tracking={CTAs.analysis.tracking}
                onClick={CTAs.analysis.onClick}
              >
                {CTAs.analysis.value ??
                  t("maxMortgageWizard.result.analysis.cta")}
              </Button>
            </Card>
          )}

          <BannerComponent
            resultType={resultType}
            hasPartner={hasPartner}
            advisorAction={CTAs?.advisor}
            continueAction={CTAs?.continue}
            thisSubjectKey={thisSubjectKey}
            theSubjectKey={theSubjectKey}
          />
        </Container>
      </Section>

      <Section>
        <Container size={container}>
          <CostCalculation
            valuesListing={<ValuesListing result={result} />}
            street={result.house?.record?.street}
            fundablePercentage={result.fundablePercentage}
            rows={result.costCalculation}
            resultType={result.resultType}
            costCalculationDescription={result.costCalculationDescription}
            hasPartner={hasPartner}
            isRenovation={isRenovation}
            isRefinance={isRefinance}
            cta={
              CTAs?.costCalculation && (
                <Button
                  border="default"
                  variant="opaque"
                  alignSelf="flex-start"
                  as={CTAs.costCalculation.href ? "a" : "button"}
                  href={CTAs.costCalculation.href}
                  tracking={CTAs.costCalculation.tracking}
                  onClick={CTAs.costCalculation.onClick}
                >
                  {CTAs.costCalculation.value ??
                    t(
                      `maxMortgageWizard.result.${resultType}.cta.costCalculation`,
                    )}
                </Button>
              )
            }
          />

          {getInterestRates && (
            <>
              <Spacing />
              <Heading
                variant="secondary"
                align="center"
                title={t("maxMortgageWizard.result.interestRates.title")}
                description={t(
                  `maxMortgageWizard.result.interestRates.description.${hasPartner ? "plural" : "singular"}`,
                )}
              />
              <InterestRatesSimple
                getInterestRates={getInterestRates}
                onFiltersChange={onInterestRateFiltersChange}
                filterRef={interestRatesFilterRef}
                hasPartner={hasPartner}
                defaultPeriod={defaultInterestRatePeriod}
                defaultNhg={defaultInterestRateNhg}
                getBankImage={getBankImage}
                cta={
                  CTAs?.interestRate && (
                    <Button
                      as={CTAs.interestRate.href ? "a" : "button"}
                      href={CTAs.interestRate.href}
                      tracking={CTAs.interestRate.tracking}
                      onClick={CTAs.interestRate.onClick}
                    >
                      {CTAs.interestRate.value ??
                        t(
                          `maxMortgageWizard.result.${resultType}.cta.interestRate`,
                        )}
                    </Button>
                  )
                }
              />
            </>
          )}

          {trailing && (
            <>
              <Spacing />
              {trailing}
            </>
          )}

          <Flex direction="column" gap="xs">
            <Text as="h4" bold>
              {t("maxMortgageWizard.result.importantInfo.title")}
            </Text>
            <Text as="p" color="currentColor50" markdown>
              {t(
                `maxMortgageWizard.result.importantInfo.description.${hasPartner ? "plural" : "singular"}`,
              )}
            </Text>
          </Flex>
        </Container>
      </Section>
    </div>
  );
};
